/**
 * File:        resources/js/components/ui/card.tsx
 * Module:      Design System · Primitives
 * Purpose:     shadcn Card primitive retuned to the DialN brand — subtler default shadow
 *              (shadow-xs), tighter default padding (p-5), and an `elevated` variant for
 *              floating surfaces that need more depth.
 *
 * Exports:
 *   - Card(divProps)                 — root container; supports `elevation` opt
 *   - CardHeader(divProps)           — title/description row (px-5 py-4)
 *   - CardTitle(divProps)            — heading text (semibold)
 *   - CardDescription(divProps)      — muted supporting copy
 *   - CardContent(divProps)          — body region (px-5)
 *   - CardFooter(divProps)           — action row (px-5)
 *
 * Elevation matrix:
 *   - default   → shadow-xs (subtler than before)
 *   - elevated  → shadow-md (use for floating surfaces, modals-in-page)
 *   - flat      → no shadow (use inside other cards)
 *
 * Side-effects:
 *   - none
 *
 * Key invariants:
 *   - All subcomponents forward refs to the underlying div.
 *   - Padding is standardized to px-5 (vertical varies by subcomponent).
 *
 * Author:      Aman Vats Sharma
 * Last-updated: 2026-06-17
 */
import * as React from "react"

import { cn } from "@/lib/utils"

type CardElevation = "default" | "elevated" | "flat"

function Card({
  className,
  elevation = "default",
  ...props
}: React.ComponentProps<"div"> & {
  elevation?: CardElevation
}) {
  return (
    <div
      data-slot="card"
      data-elevation={elevation}
      className={cn(
        "bg-card text-card-foreground flex flex-col gap-4 rounded-xl border py-5",
        elevation === "default" && "shadow-xs",
        elevation === "elevated" && "shadow-md",
        elevation === "flat" && "shadow-none",
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn("flex flex-col gap-1.5 px-5 py-4", className)}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-5", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-5", className)}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  type CardElevation,
}